import * as XLSX from 'xlsx';
import * as React from 'react';
import { FileText, Image, FileVideo, Music, Archive, File } from 'lucide-react';
import { sanitizeExportValue } from '../../../common/utils';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Interface for Incident data (updated to match FireSafetyIncident.tsx)
interface IncidentData {
  id?: string;
  region?: string;
  hospitalName: string;
  reportDate: string;
  incidentDate: string;
  reportedBy: string;
  placeOfOccurrence?: string;
  whoWasInvolved?: string;
  patientId?: string;
  patientName?: string;
  patientAge?: number | null;
  patientGender?: string;
  department: string;
  physicianHod?: string;
  criticalIncident?: boolean;
  eventDocumentedOnChart?: boolean;
  seenByAttendingPhysician?: boolean;
  attendingPhysicianInformed?: boolean;
  familyRelativesInformed?: boolean;
  emergencyPhysicianInformed?: boolean;
  whatWentWrong?: string;
  immediateActionTaken?: string;
  whatShouldHaveHappened?: string;
  status: string;
  attachments?: any[];
}

// Interface for SendModal data (from SendModal.tsx)
interface SendModalData {
  id?: string;
  incident: string;
  actionType: string;
  actionBy: string;
  category: string;
  subCategory: string;
  criteria: string;
  comment: string;
  forwardedTo: string[];
  cc: string[];
}

// Interface for RCA Modal data (from RCAModal.tsx)
interface RCAData {
  id?: string;
  incident: string;
  rcaType?: string;
  rcaMethod?: string;
  sacMatrix: string[];
  rcaTeam: string;
  rcaTeamMembers?: any[];
  uhid: string;
  patientName: string;
  patientAge: string;
  patientGender: string;
  dateOfAdmission: string;
  consultant: string;
  eventDateTime: string;
  eventDescription: string;
  medications: string;
  autopsyResults: string;
  medicalHistory: string;
  rootCauses: string[];
  correctiveActions: string[];
  preventiveMeasures: string[];
  status: string;
  rcaDoneDate?: string;
  location?: string;
  severity?: string;
  incidentSynopsis?: string;
  observations?: string;
  summary?: string;
  identifiedCause?: string;
  timelineEvents?: any[];
  witnessStatements?: any[];
  consequences?: any[];
  fishboneAnalysis?: any;
  whyWhyAnalysis?: any[];
  contributingFactors?: any[];
  rootCauseIdentified?: boolean;
  rootCauseDescription?: string;
  conclusion?: string;
  correctiveActionPlans?: any[];
  preventiveActions?: any[];
  attachments: any[];
  completionDate: string;
}

// ============================================================================
// DATA FORMATTING UTILITIES
// ============================================================================

/**
 * Safely formats values for display, handling null, undefined, arrays, booleans, and dates
 * @param value - The value to format
 * @returns Formatted string representation
 */
export const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  if (Array.isArray(value)) {
    const formatted = value
      .map((item) =>
        typeof item === 'object' && item !== null
          ? JSON.stringify(item)
          : String(item)
      )
      .join(', ');
    return sanitizeExportValue(formatted);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') return sanitizeExportValue(JSON.stringify(value));
  const trimmed = String(value).trim() || 'N/A';
  return sanitizeExportValue(trimmed);
};


/**
 * Formats a date string or Date object to a localized date string
 * @param date - Date string or Date object
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

/**
 * Gets current date as ISO string
 * @returns Current date in ISO format
 */
export const getCurrentISODate = (): string => {
  return new Date().toISOString();
};

// ============================================================================
// ARRAY HANDLING UTILITIES
// ============================================================================

/**
 * Converts comma-separated string to array, handling empty values
 * @param value - Comma-separated string
 * @returns Array of trimmed strings
 */
export const handleArrayFieldChange = (value: string): string[] => {
  if (!value || value.trim() === "") {
    return [];
  }
  return value.split(",").map((item) => item.trim()).filter((item) => item !== "");
};

/**
 * Safely displays array values as comma-separated string
 * @param arrayValue - Array to display
 * @returns Comma-separated string or 'N/A' if empty
 */
export const displayArrayValue = (arrayValue: string[]): string => {
  if (!arrayValue || arrayValue.length === 0) {
    return "N/A";
  }
  return arrayValue.join(", ");
};

// ============================================================================
// FILE HANDLING UTILITIES
// ============================================================================

/**
 * Extracts file extension from filename
 * @param filename - The filename to extract extension from
 * @returns File extension in lowercase
 */
export const getFileExtension = (filename: string): string => {
  return filename.split(".").pop()?.toLowerCase() || "";
};

/**
 * Returns appropriate file icon JSX element based on file extension
 * @param filename - The filename to get icon for
 * @param iconProps - Optional props to pass to the icon
 * @returns JSX icon element
 */
export const getFileIcon = (filename: string, iconProps = { size: 16, className: "text-gray-600" }): React.ReactElement => {
  const ext = getFileExtension(filename);

  switch (ext) {
    case "pdf":
    case "doc":
    case "docx":
    case "txt":
      return React.createElement(FileText, iconProps);
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "bmp":
      return React.createElement(Image, iconProps);
    case "mp4":
    case "avi":
    case "mov":
      return React.createElement(FileVideo, iconProps);
    case "mp3":
    case "wav":
      return React.createElement(Music, iconProps);
    case "zip":
    case "rar":
      return React.createElement(Archive, iconProps);
    default:
      return React.createElement(File, iconProps);
  }
};

/**
 * Checks if a file can be previewed in the browser
 * @param filename - The filename to check
 * @returns True if file can be previewed
 */
export const canPreviewFile = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  return ["jpg", "jpeg", "png", "pdf", "txt"].includes(ext);
};

// ============================================================================
// QUERY PARAMETER UTILITIES
// ============================================================================

/**
 * Builds query parameters object from filters, excluding empty values
 * @param filters - Object containing filter values
 * @returns Clean parameters object
 */
export const buildQueryParams = (filters: Record<string, any>): Record<string, any> => {
  const params: Record<string, any> = {};
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '' && value !== 'All') {
      params[key] = value;
    }
  });
  
  return params;
};

/**
 * Builds URL search params string from object
 * @param params - Parameters object
 * @returns URL search params string
 */
export const buildURLSearchParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
};

// ============================================================================
// FORM VALIDATION UTILITIES
// ============================================================================

/**
 * Validates required fields in a form data object
 * @param data - Form data object
 * @param requiredFields - Array of required field names
 * @returns Object with isValid boolean and errors array
 */
export const validateFormData = (
  data: Record<string, any>, 
  requiredFields: string[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  requiredFields.forEach(field => {
    const value = data[field];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors.push(`${field} is required`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates email format
 * @param email - Email string to validate
 * @returns True if email format is valid
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// ============================================================================
// EXCEL GENERATION UTILITIES
// ============================================================================

/**
 * Creates an Excel worksheet for incident details
 * @param incident - Incident data
 * @returns Excel worksheet
 */
const createIncidentSheet = (incident: IncidentData): XLSX.WorkSheet => {
  const headers = [
    'Region',
    'Hospital Name',
    'Report Date',
    'Incident Date',
    'Reported By',
    'Place of Occurrence',
    'Who Was Involved',
    'Patient ID',
    'Patient Name',
    'Patient Age',
    'Gender',
    'Department',
    'Physician/HOD',
    'Critical Incident',
    'Event Documented',
    'Seen by Physician',
    'Physician Informed',
    'Family Informed',
    'Emergency Physician Informed',
    'What Went Wrong',
    'Immediate Action',
    'What Should Have Happened',
    'Status',
    'Attachments'
  ];

  const data = [[
    formatValue(incident.region),
    formatValue(incident.hospitalName),
    formatValue(incident.reportDate),
    formatValue(incident.incidentDate),
    formatValue(incident.reportedBy),
    formatValue(incident.placeOfOccurrence),
    formatValue(incident.whoWasInvolved),
    formatValue(incident.patientId),
    formatValue(incident.patientName),
    formatValue(incident.patientAge),
    formatValue(incident.patientGender),
    formatValue(incident.department),
    formatValue(incident.physicianHod),
    formatValue(incident.criticalIncident),
    formatValue(incident.eventDocumentedOnChart),
    formatValue(incident.seenByAttendingPhysician),
    formatValue(incident.attendingPhysicianInformed),
    formatValue(incident.familyRelativesInformed),
    formatValue(incident.emergencyPhysicianInformed),
    formatValue(incident.whatWentWrong),
    formatValue(incident.immediateActionTaken),
    formatValue(incident.whatShouldHaveHappened),
    formatValue(incident.status),
    formatValue(incident.attachments?.map(a => a.filename || a.name).filter(Boolean))
  ]];

  const ws = XLSX.utils.json_to_sheet([{}], { header: headers });
  XLSX.utils.sheet_add_json(ws, data, { skipHeader: true, origin: 'A2' });
  return ws;
};

/**
 * Creates an Excel worksheet for SendModal data
 * @param sendModal - SendModal data
 * @returns Excel worksheet
 */
const createSendModalSheet = (sendModal: SendModalData): XLSX.WorkSheet => {
  const headers = [
    'Incident ID',
    'Action Type',
    'Action By',
    'Category',
    'Sub Category',
    'Criteria',
    'Comment',
    'Forwarded To',
    'CC'
  ];

  const data = [[
    formatValue(sendModal.incident),
    formatValue(sendModal.actionType),
    formatValue(sendModal.actionBy),
    formatValue(sendModal.category),
    formatValue(sendModal.subCategory),
    formatValue(sendModal.criteria),
    formatValue(sendModal.comment),
    formatValue(sendModal.forwardedTo),
    formatValue(sendModal.cc)
  ]];

  const ws = XLSX.utils.json_to_sheet([{}], { header: headers });
  XLSX.utils.sheet_add_json(ws, data, { skipHeader: true, origin: 'A2' });
  return ws;
};

/**
 * Creates an Excel worksheet for RCA data
 * @param rca - RCA data
 * @returns Excel worksheet
 */
const createRCASheet = (rca: RCAData): XLSX.WorkSheet => {
  const headers = [
    'Incident ID',
    'RCA Type',
    'RCA Method',
    'RCA Status',
    'RCA Done Date',
    'RCA Team Members',
    'Location',
    'Severity',
    'Incident Synopsis',
    'Observations',
    'Summary',
    'Identified Cause',
    'Timeline Events',
    'Witness Statements',
    'Consequences',
    'Fishbone Analysis',
    'Why Why Analysis',
    'Contributing Factors',
    'Root Cause Identified',
    'Root Cause Description',
    'Conclusion',
    'Corrective Action Plans',
    'Preventive Actions',
    'SAC Matrix',
    'RCA Team',
    'UHID',
    'Patient Name',
    'Patient Age',
    'Gender',
    'Date of Admission',
    'Consultant',
    'Event DateTime',
    'Event Description',
    'Medications',
    'Autopsy Results',
    'Medical History',
    'Root Causes',
    'Corrective Actions',
    'Preventive Measures',
    'Completion Date',
    'Attachments'
  ];

  const data = [[
    formatValue(rca.incident),
    formatValue(rca.rcaType),
    formatValue(rca.rcaMethod),
    formatValue(rca.status),
    formatValue(rca.rcaDoneDate),
    formatValue(rca.rcaTeamMembers),
    formatValue(rca.location),
    formatValue(rca.severity),
    formatValue(rca.incidentSynopsis),
    formatValue(rca.observations),
    formatValue(rca.summary),
    formatValue(rca.identifiedCause),
    formatValue(rca.timelineEvents),
    formatValue(rca.witnessStatements),
    formatValue(rca.consequences),
    formatValue(rca.fishboneAnalysis),
    formatValue(rca.whyWhyAnalysis),
    formatValue(rca.contributingFactors),
    formatValue(rca.rootCauseIdentified),
    formatValue(rca.rootCauseDescription),
    formatValue(rca.conclusion),
    formatValue(rca.correctiveActionPlans),
    formatValue(rca.preventiveActions),
    formatValue(rca.sacMatrix),
    formatValue(rca.rcaTeam),
    formatValue(rca.uhid),
    formatValue(rca.patientName),
    formatValue(rca.patientAge),
    formatValue(rca.patientGender),
    formatValue(rca.dateOfAdmission),
    formatValue(rca.consultant),
    formatValue(rca.eventDateTime),
    formatValue(rca.eventDescription),
    formatValue(rca.medications),
    formatValue(rca.autopsyResults),
    formatValue(rca.medicalHistory),
    formatValue(rca.rootCauses),
    formatValue(rca.correctiveActions),
    formatValue(rca.preventiveMeasures),
    formatValue(rca.completionDate),
    formatValue(rca.attachments?.map(a => a.filename || a.name).filter(Boolean))
  ]];

  const ws = XLSX.utils.json_to_sheet([{}], { header: headers });
  XLSX.utils.sheet_add_json(ws, data, { skipHeader: true, origin: 'A2' });
  return ws;
};

/**
 * Generates and downloads an Excel file with incident data
 * @param incident - Incident data
 * @param sendModal - Optional SendModal data
 * @param rca - Optional RCA data
 */
export const generateIncidentExcel = (
  incident: IncidentData,
  sendModal?: SendModalData,
  rca?: RCAData
): void => {
  const wb = XLSX.utils.book_new();
  
  // Always include Incident Details sheet
  const incidentSheet = createIncidentSheet(incident);
  XLSX.utils.book_append_sheet(wb, incidentSheet, 'Incident Details');

  // Add SendModal sheet for pending or resolved status
  if (incident.status.toLowerCase() === 'pending' || incident.status.toLowerCase() === 'resolved') {
    if (sendModal) {
      const sendModalSheet = createSendModalSheet(sendModal);
      XLSX.utils.book_append_sheet(wb, sendModalSheet, 'SendModal Data');
    }
  }

  // Add RCA sheet whenever RCA data is available
  if (rca) {
    const rcaSheet = createRCASheet(rca);
    XLSX.utils.book_append_sheet(wb, rcaSheet, 'RCAModal Data');
  }

  // Auto-size columns for all sheets
  wb.SheetNames.forEach(sheetName => {
    const ws = wb.Sheets[sheetName];
    const colWidths = (headers: string[]): { wch: number }[] => {
      return headers.map((header, i) => {
        const dataWidth = ws['!data']?.map((row: any[]) => String(row[i] || '').length) || [];
        return { wch: Math.max(header.length, ...dataWidth, 10) };
      });
    };
    ws['!cols'] = colWidths(Object.keys(ws).filter(k => k.match(/^[A-Z]+1$/)));
  });

  // Generate filename with incident number and timestamp
  const incidentNumber = incident.id || 'incident';
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `Incident_${incidentNumber}_${timestamp}.xlsx`;

  // Write and download the file
  XLSX.writeFile(wb, filename);
};

// ============================================================================
// MISCELLANEOUS UTILITIES
// ============================================================================

/**
 * Debounces a function call
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Capitalizes the first letter of a string
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Truncates text to specified length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Checks if a value is empty (null, undefined, empty string, or empty array)
 * @param value - Value to check
 * @returns True if value is empty
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Safely gets a nested property from an object
 * @param obj - Object to get property from
 * @param path - Dot-separated path to property
 * @param defaultValue - Default value if property doesn't exist
 * @returns Property value or default value
 */
export const getNestedProperty = (obj: any, path: string, defaultValue: any = undefined): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};
